import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import { useAuth } from '../../../lib/auth';
import CategoriesTabs from '../../../components/tabs/CategoriesTabs.tsx';
import { WorksheetCard } from '../../../components/cards/WorksheetCard.tsx';
import { EmptyState } from '../../../components/emptyStates/EmptyState.tsx';
import { useParams } from 'react-router';
import { useFetchWorksheets } from '../../../lib/fetches.ts';
import { Color, Worksheet } from '../../../lib/types.ts';
import { API_URL } from '../../../lib/api.ts';
import API from '../../../lib/api.ts';
import Skeleton from '../../../components/Skeleton.tsx';

function getAgeCategoryKey(w: Worksheet) {
    return w.age_category?.slug ?? 'ostatni';
}

function getAgeCategoryName(w: Worksheet) {
    return w.age_category?.name ?? 'Ostatní';
}

function getCategorySlug(w: Worksheet) {
    return w.category?.slug ?? w.category_slug ?? '';
}

function getCategoryName(w: Worksheet) {
    return w.category?.name ?? w.category_name ?? 'Kategorie';
}

function getCategoryColor(w: Worksheet) {
    return w.category?.color ?? w.category_color ?? ('turquoise' as Color);
}

function getOpenPdfUrl(slug: string) {
    return `${API_URL}/worksheets/${slug}/pdf`;
}

function filenameFromContentDisposition(value: string | undefined) {
    if (!value) return undefined;
    const match =
        /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(
            value
        );
    const encoded = match?.[1];
    const quoted = match?.[2];
    const bare = match?.[3];
    const raw = encoded ?? quoted ?? bare;
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw.trim());
    } catch {
        return raw.trim();
    }
}

async function downloadWorksheetPdf(slug: string) {
    const res = await API.get(`/worksheets/${slug}/pdf`, {
        responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const name =
        filenameFromContentDisposition(res.headers?.['content-disposition']) ??
        `${slug}.pdf`;

    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function WorksheetsPage() {
    useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'WorksheetsPage',
    });

    const { category = '' } = useParams();

    const { data: worksheets, isLoading } = useFetchWorksheets();

    const filteredWorksheets =
        category.length > 0
            ? (worksheets ?? []).filter((w) => getCategorySlug(w) === category)
            : worksheets ?? [];

    const grouped = new Map<
        string,
        { key: string; name: string; items: Worksheet[] }
    >();
    const sectionKeys: string[] = [];

    for (const w of filteredWorksheets) {
        const key = getAgeCategoryKey(w);
        const existing = grouped.get(key);
        if (!existing) {
            grouped.set(key, { key, name: getAgeCategoryName(w), items: [w] });
            sectionKeys.push(key);
            continue;
        }
        existing.items.push(w);
    }

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'mx-auto box-content max-w-6xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="mb-12">
                    <h1>Pracovní listy</h1>
                    <p className="text-lg text-ink-800">
                        Vyber si, co dnes procvičíš.
                    </p>
                </div>

                <div className="mb-12">
                    <CategoriesTabs basePath="/pracovni-listy" />
                </div>

                {isLoading ? (
                    <div className="space-y-12 pb-16">
                        {[0, 1, 2].map((idx) => (
                            <section key={idx}>
                                <Skeleton className="mb-6 h-7 w-48 rounded-xl" />
                                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                                    <Skeleton className="h-28 w-full rounded-3xl" />
                                    <Skeleton className="h-28 w-full rounded-3xl" />
                                </div>
                            </section>
                        ))}
                    </div>
                ) : sectionKeys.length === 0 ? (
                    <EmptyState>
                        Tato kategorie zatím neobsahuje žádné pracovní listy.
                    </EmptyState>
                ) : (
                    <div className="space-y-12 pb-16">
                        {sectionKeys.map((key) => {
                            const section = grouped.get(key);
                            if (!section) return null;

                            return (
                                <section key={key}>
                                    <h4 className="mb-6">{section.name}</h4>
                                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                                    {section.items.map((w) => (
                                        <WorksheetCard
                                            key={w.slug}
                                            title={w.name}
                                            description={w.description}
                                            badge={getCategoryName(w)}
                                            color={getCategoryColor(w)}
                                            openHref={getOpenPdfUrl(w.slug)}
                                            onDownload={() =>
                                                downloadWorksheetPdf(w.slug)
                                            }
                                        />
                                    ))}
                                </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </main>
        </AuthLayout>
    );
}

